'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import { DEFAULT_CONTACT_INFO } from '@/lib/constants';
import { getApiUrl, getCacheHeaders } from '@/lib/site-config';

interface ContactInfo {
  company_name: string;
  address_line1: string;
  address_line2: string;
  phone1: string;
  phone2: string;
  email: string;
  opening_hours: string;
}

export default function Footer() {
  const [contactInfo, setContactInfo] = useState<ContactInfo>(DEFAULT_CONTACT_INFO);

  useEffect(() => {
    // Pobierz dane kontaktowe z tego samego API co strona /contact
    const fetchContactInfo = async () => {
      try {
        const timestamp = Date.now();
        const baseUrl = getApiUrl('contactInfo');
        const separator = baseUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${baseUrl}${separator}t=${timestamp}`, {
          method: 'GET',
          headers: {
            ...getCacheHeaders()
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.success && data.contactInfo) {
            setContactInfo(data.contactInfo);
          }
        }
      } catch (error) {
        console.error('Błąd podczas pobierania informacji kontaktowych:', error);
      }
    };

    fetchContactInfo();
  }, []);

  return (
    <footer className="bg-[rgb(6,40,95)] text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex flex-col items-center gap-5 mb-4">
              <div className="relative w-24 h-24">
                <Image
                  src="/images/logo.webp"
                  alt="Ośrodek Szkolenia Morskiego Logo"
                  fill
                  className="object-contain"
                  priority
                />
              </div>
              <h3 className="text-2xl font-bold text-white text-center">{contactInfo.company_name}</h3>
            </div>
            <p className="text-gray-200 mb-4 text-center">
              Twoja Droga Na Morze<br />
              Zaczyna Się Tutaj
            </p>
            <div className="flex space-x-4 justify-center">
              <a href="https://www.facebook.com/szkoleniamorskie" target="_blank" rel="noopener noreferrer" aria-label="Facebook — Ośrodek Szkolenia Morskiego LIBRA" className="text-gray-300 hover:text-white transition-colors">
                <FaFacebook size={24} aria-hidden="true" />
              </a>
              <a href="https://www.instagram.com/szkoleniamorskie/#" target="_blank" rel="noopener noreferrer" aria-label="Instagram — Ośrodek Szkolenia Morskiego LIBRA" className="text-gray-300 hover:text-white transition-colors">
                <FaInstagram size={24} aria-hidden="true" />
              </a>
            </div>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-4">Szybkie linki</h4>
            <ul className="space-y-2">
              <li>
                <Link href="/about" className="text-gray-200 hover:text-white transition-colors">
                  O nas
                </Link>
              </li>
              <li>
                <Link href="/training" className="text-gray-200 hover:text-white transition-colors">
                  Katalog szkoleń
                </Link>
              </li>
              <li>
                <Link href="/training/stcw-polska" className="text-gray-200 hover:text-white transition-colors">
                  Kursy STCW w Polsce
                </Link>
              </li>
              <li>
                <Link href="/motorowodne" className="text-gray-200 hover:text-white transition-colors">
                  Patenty motorowodne
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-gray-200 hover:text-white transition-colors">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-4">Kontakt</h4>
            <div className="text-gray-200">
              <div className="mb-2">{contactInfo.company_name}</div>
              <div className="mb-2">{contactInfo.address_line1}</div>
              <div className="mb-2">{contactInfo.address_line2}</div>
              <div className="mb-2">{contactInfo.phone1}</div>
              <div className="mb-2">{contactInfo.phone2}</div>
              <div className="mb-2 flex">
                <span className="mr-1">Email:</span>
                <a href={`mailto:${contactInfo.email}`} className="text-gray-200 hover:text-white overflow-hidden text-overflow-ellipsis inline-block">
                  {contactInfo.email}
                </a>
              </div>
              
              <div className="mt-4 mb-2">
                <span className="text-sm font-semibold">Godziny otwarcia:</span>
              </div>
              {contactInfo.opening_hours && contactInfo.opening_hours.split('\n').map((line, index) => (
                <div key={index} className="mb-1">{line}</div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-[rgb(16,50,115)] text-center text-gray-300">
          <p>&copy; {new Date().getFullYear()} {contactInfo.company_name}. Wszelkie prawa zastrzeżone.</p>
          <a
            href="https://selkano.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 rounded-lg border border-white/15 px-4 py-2 mt-3 transition-all hover:border-white/30 hover:shadow-sm"
          >
            <span className="text-[11px] text-gray-400 transition-colors group-hover:text-gray-300">
              Design & Dev
            </span>
            <span className="text-sm font-semibold text-gray-300 transition-colors group-hover:text-white">
              Selkano
            </span>
            <svg
              className="h-3 w-3 text-gray-500 transition-all group-hover:translate-x-0.5 group-hover:text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          </a>
        </div>
      </div>
    </footer>
  );
} 